import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {Empty, NotFound, isLoaded, AsyncPage} from 'Core/models/Async';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {EnrollmentDiscrepantStatus} from 'Shared/constants/EnrollmentDiscrepantStatus';
import PagedView from 'Shared/components/PagedView/PagedView';
import {getEmployerEnrollments, changeEnrollmentsFilters, EmployerEnrollmentFilters} from 'Enrollment/ducks/employerEnrollment';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {withStringFilterProperties, withInequalityFilterProperties} from 'Shared/components/PagedView/Filter';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import './EmployerEnrollments.sass';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIconWithCustomError} from 'Shared/components/Toolbar/ToolbarHeader';
import {Tooltip} from '@material-ui/core';
import * as O from 'fp-ts/lib/Option';
import agencyFilterSelector from 'Users/selectors/selectAgencyFilters';

const EmployerEnrollments = () => {
  const dispatch = useDispatch();
  const {employerEnrollmentPage, filter} = useSelector((state: ReducerState) => state.employerEnrollment);

  const changeFilter = withValueChangedHook(filter, changeEnrollmentsFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: filter,
  };
  const agencyFilters = useSelector(agencyFilterSelector);

  useEffect(() => {
    dispatch(getEmployerEnrollments(undefined, filter));
    let agentData = O.toUndefined(agencyFilters);
    filter.isNewYork = agentData?.isNewYork!;
    filter.agentNumbers = agentData?.agentNumbers.join()!;
    filter.encryptedIdentifier = agentData?.encryptedIdentifier!;
  }, [dispatch, filter, agencyFilters]);

  const getEmployerEnrollmentDetails = (
    count: number,
    accountNumber?: string | null,
    discrepantStatus?: EnrollmentDiscrepantStatus
  ) => {
    return count > 0 ? (
      <Link
        className="enrollment-app-link"
        to={EnrollmentRouteConstants.EMPLOYER_ENROLLMENTS_DETAILS(accountNumber, discrepantStatus)}
      >
        {count}
      </Link>
    ) : (
      count
    );
  };

  return (
    <>
      <ModularHost>
        <ToolbarHeader title="Account Enrollments">
        <ExportToolbarHeaderIconWithCustomError
              title="Export Account Enrollments"
              downloadedFileName="Account Enrollments"
              extension={FileExtension.CSV}
              relativeFileLocation={`/enrollmentFiles/enrollment/employers/csv/${addFilterParameters({
                exportToCsv: true, ...filter,
              })}`}
              totalRecordCount={isLoaded(employerEnrollmentPage) ? employerEnrollmentPage.Data.totalCount : 0}
            />
        </ToolbarHeader>
        <PagedView
          pagedViewKey="employerEnrollments"
          loadMore={page => dispatch(getEmployerEnrollments(page, filter))}
          data={employerEnrollmentPage}
          additionalFilterProperties={[
            {
              title: 'Agent Number',
              filter: withStringFilterProperties<EmployerEnrollmentFilters>(filterProperties, 'agentNumber'),
            }
          ]}
          
        >
          {columnProps => (
            <>
              <Column
                {...columnProps}
                title="Account Number"
                filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
                default
              >
                {enrollment => enrollment.accountNumber}
              </Column>
              <Column
                {...columnProps}
                title="In Progress"
                filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfInProgressRecords')}
                default
              >
                {enrollment =>
                  getEmployerEnrollmentDetails(
                    enrollment.numberOfInProgressRecords!,
                    enrollment.accountNumber,
                    EnrollmentDiscrepantStatus.InProgress
                  )
                }
              </Column>
              <Column
                {...columnProps}
                title="Successful"
                filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfSuccessfulRecords')}
                default
              >
                {enrollment =>
                  getEmployerEnrollmentDetails(
                    enrollment.numberOfSuccessfulRecords!,
                    enrollment.accountNumber!.toString(),
                    EnrollmentDiscrepantStatus.Successful
                  )
                }
              </Column>
              <Column
                {...columnProps}
                title="Unsuccessful"
                filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfDiscrepancies')}
              >
                {enrollment =>
                  getEmployerEnrollmentDetails(
                    enrollment.numberOfDiscrepancies!,
                    enrollment.accountNumber,
                    EnrollmentDiscrepantStatus.Total
                  )
                }
              </Column>
              <Column
                {...columnProps}
                title="Outstanding Unsuccessful"
                filterProperties={withInequalityFilterProperties(filterProperties, 'numberOfOutstandingDiscrepancies')}
                default
              >
                {enrollment =>
                  getEmployerEnrollmentDetails(
                    enrollment.numberOfOutstandingDiscrepancies!,
                    enrollment.accountNumber,
                    EnrollmentDiscrepantStatus.Outstanding
                  )
                }
              </Column>
              <Column
                {...columnProps}
                title="Age of Oldest Unsuccessful"
                filterProperties={withInequalityFilterProperties(
                  filterProperties,
                  'ageOfOldestOutstandingDiscrepancyInDays'
                )}
                default
              >
                {enrollment => (
                  <Tooltip title={enrollment.ageOfOldestOutstandingDiscrepancyInFractionalDays!}>
                    <div>{enrollment.ageOfOldestOutstandingDiscrepancyInDays}</div>
                  </Tooltip>
                )}
              </Column>
            </>
          )}
        </PagedView>
      </ModularHost>
    </>
  );
};

export default EmployerEnrollments;
